import React, { useEffect, useState } from 'react';
import { AlertCircle } from 'lucide-react';
import SearchableSelect from './SearchableSelect';
import { UFS, buscarMunicipios } from '../services/localidades';

interface CampoCidadeUfProps {
  uf: string;
  cidade: string;
  onChange: (valores: { uf: string; cidade: string }) => void;
  disabled?: boolean;
  required?: boolean;
}

/**
 * Par UF + Cidade, com a lista de municipios saindo do IBGE.
 *
 * Digitar cidade a mao produz "Sao Paulo", "SÃO PAULO" e "sao paulo" como se
 * fossem lugares diferentes, e relatorio agrupado por cidade fica inutil. Com a
 * lista do IBGE o nome sai sempre igual, escrito do mesmo jeito.
 *
 * Se o IBGE nao responder, o campo vira texto livre em vez de travar: cadastrar
 * a empresa com a cidade digitada e melhor do que nao conseguir cadastrar.
 */
const CampoCidadeUf: React.FC<CampoCidadeUfProps> = ({
  uf,
  cidade,
  onChange,
  disabled = false,
  required = false,
}) => {
  const [municipios, setMunicipios] = useState<string[]>([]);
  const [carregando, setCarregando] = useState(false);
  const [falhou, setFalhou] = useState(false);

  useEffect(() => {
    if (!uf) {
      setMunicipios([]);
      setFalhou(false);
      return;
    }

    // Trocar de UF rapido dispara buscas em sequencia; sem este descarte, a
    // resposta de uma UF antiga pode chegar depois e sobrescrever a lista atual.
    let atual = true;
    setCarregando(true);
    setFalhou(false);

    buscarMunicipios(uf)
      .then((lista) => {
        if (atual) setMunicipios(lista);
      })
      .catch(() => {
        if (atual) {
          setMunicipios([]);
          setFalhou(true);
        }
      })
      .finally(() => {
        if (atual) setCarregando(false);
      });

    return () => {
      atual = false;
    };
  }, [uf]);

  // O CEP preenche a cidade a partir do ViaCEP, que ocasionalmente grafa o nome
  // diferente do IBGE. Sem acrescentar o valor recebido as opcoes, o select
  // ficaria vazio mostrando um campo "sem selecao" logo depois de preencher.
  const opcoesCidade = municipios.includes(cidade) || !cidade
    ? municipios
    : [cidade, ...municipios];

  return (
    <>
      <div>
        <SearchableSelect
          label="UF"
          required={required}
          disabled={disabled}
          value={uf}
          options={UFS.map((u) => ({ value: u.sigla, label: `${u.sigla} — ${u.nome}` }))}
          placeholder="Selecione o estado..."
          onChange={(novaUf) => {
            // Cidade de outro estado nao faz sentido: limpa junto.
            onChange({ uf: novaUf, cidade: novaUf === uf ? cidade : '' });
          }}
        />
      </div>

      <div>
        {falhou ? (
          <>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Cidade {required && <span className="text-red-500">*</span>}
            </label>
            <input
              value={cidade}
              disabled={disabled}
              onChange={(e) => onChange({ uf, cidade: e.target.value })}
              className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
            />
            <p className="mt-1 flex items-center gap-1 text-xs text-amber-600">
              <AlertCircle className="h-3 w-3" />
              Lista de cidades indisponivel. Digite o nome.
            </p>
          </>
        ) : (
          <SearchableSelect
            label={carregando ? 'Cidade (carregando...)' : 'Cidade'}
            required={required}
            disabled={disabled || !uf || carregando}
            value={cidade}
            options={opcoesCidade.map((c) => ({ value: c, label: c }))}
            placeholder={uf ? 'Selecione a cidade...' : 'Selecione a UF primeiro'}
            onChange={(novaCidade) => onChange({ uf, cidade: novaCidade })}
          />
        )}
      </div>
    </>
  );
};

export default CampoCidadeUf;
